import {
  PERMISSIONS,
  permissionSatisfies,
  type Permission,
} from '@terra/shared-authorization';
import {
  TargetTypeSchema,
  type TargetTypeType,
} from '@terra/shared-schemas-orchestrator';
import {
  NOTIFICATION_ENTITY_TYPE,
  NOTIFICATION_PRIORITY_TIER,
  PLATFORM_NOTIFICATION_ACTION_TYPE,
  PLATFORM_NOTIFICATION_HEALTH_TYPE,
  type NotificationEntityType,
  type NotificationPriorityTier,
  type PlatformNotificationActionType,
  type PlatformNotificationHealthType,
} from './notification-center.constants';

const SHARED_PLATFORM_NOTIFICATION_ACTION_TYPES =
  new Set<PlatformNotificationActionType>([
    PLATFORM_NOTIFICATION_ACTION_TYPE.BUG_BOUNTY_SIGNAL_IMPORT_SUMMARY,
  ]);

const SHARED_PLATFORM_NOTIFICATION_HEALTH_TYPES =
  new Set<PlatformNotificationHealthType>([
    PLATFORM_NOTIFICATION_HEALTH_TYPE.SOURCE_CODE_CONNECTOR_OFFLINE,
    PLATFORM_NOTIFICATION_HEALTH_TYPE.KNOWLEDGE_SOURCE_SYNC_FAILED,
    PLATFORM_NOTIFICATION_HEALTH_TYPE.INTERNAL_CONNECTOR_OFFLINE,
    PLATFORM_NOTIFICATION_HEALTH_TYPE.CLOUD_IP_UNASSOCIATED,
  ]);

export const isSharedActionType = ({
  type,
}: {
  type: PlatformNotificationActionType;
}): boolean => SHARED_PLATFORM_NOTIFICATION_ACTION_TYPES.has(type);

export const isSharedHealthType = ({
  type,
}: {
  type: PlatformNotificationHealthType;
}): boolean => SHARED_PLATFORM_NOTIFICATION_HEALTH_TYPES.has(type);

export type NotificationActionCatalogEntry = {
  type: PlatformNotificationActionType;
  priorityTier: NotificationPriorityTier;
  requiredPermission: Permission;
};

export const PLATFORM_ACTION_CATALOG: Record<
  PlatformNotificationActionType,
  NotificationActionCatalogEntry
> = {
  [PLATFORM_NOTIFICATION_ACTION_TYPE.TRIAGE_NEW]: {
    type: PLATFORM_NOTIFICATION_ACTION_TYPE.TRIAGE_NEW,
    priorityTier: NOTIFICATION_PRIORITY_TIER.P2,
    requiredPermission: PERMISSIONS.MANAGE_FINDINGS.name,
  },
  [PLATFORM_NOTIFICATION_ACTION_TYPE.CONTINUE_REMEDIATION]: {
    type: PLATFORM_NOTIFICATION_ACTION_TYPE.CONTINUE_REMEDIATION,
    priorityTier: NOTIFICATION_PRIORITY_TIER.P3,
    requiredPermission: PERMISSIONS.MANAGE_FINDINGS.name,
  },
  [PLATFORM_NOTIFICATION_ACTION_TYPE.REVIEW_RETEST_STILL_VULNERABLE]: {
    type: PLATFORM_NOTIFICATION_ACTION_TYPE.REVIEW_RETEST_STILL_VULNERABLE,
    priorityTier: NOTIFICATION_PRIORITY_TIER.P2,
    requiredPermission: PERMISSIONS.VIEW_FINDINGS.name,
  },
  [PLATFORM_NOTIFICATION_ACTION_TYPE.INVESTIGATE_RETEST_FAILURE]: {
    type: PLATFORM_NOTIFICATION_ACTION_TYPE.INVESTIGATE_RETEST_FAILURE,
    priorityTier: NOTIFICATION_PRIORITY_TIER.P1,
    requiredPermission: PERMISSIONS.VIEW_FINDINGS.name,
  },
  [PLATFORM_NOTIFICATION_ACTION_TYPE.SEVERITY_CHANGE_APPROVED]: {
    type: PLATFORM_NOTIFICATION_ACTION_TYPE.SEVERITY_CHANGE_APPROVED,
    priorityTier: NOTIFICATION_PRIORITY_TIER.P2,
    requiredPermission: PERMISSIONS.VIEW_FINDINGS.name,
  },
  [PLATFORM_NOTIFICATION_ACTION_TYPE.SEVERITY_CHANGE_REJECTED]: {
    type: PLATFORM_NOTIFICATION_ACTION_TYPE.SEVERITY_CHANGE_REJECTED,
    priorityTier: NOTIFICATION_PRIORITY_TIER.P2,
    requiredPermission: PERMISSIONS.VIEW_FINDINGS.name,
  },
  [PLATFORM_NOTIFICATION_ACTION_TYPE.REVIEW_NEW_MEMORY]: {
    type: PLATFORM_NOTIFICATION_ACTION_TYPE.REVIEW_NEW_MEMORY,
    priorityTier: NOTIFICATION_PRIORITY_TIER.P2,
    requiredPermission: PERMISSIONS.VIEW_TESTS.name,
  },
  [PLATFORM_NOTIFICATION_ACTION_TYPE.NEW_SCHEDULED_REPORT]: {
    type: PLATFORM_NOTIFICATION_ACTION_TYPE.NEW_SCHEDULED_REPORT,
    priorityTier: NOTIFICATION_PRIORITY_TIER.P3,
    requiredPermission: PERMISSIONS.VIEW_FINDINGS.name,
  },
  [PLATFORM_NOTIFICATION_ACTION_TYPE.BUG_BOUNTY_SIGNAL_IMPORT_SUMMARY]: {
    type: PLATFORM_NOTIFICATION_ACTION_TYPE.BUG_BOUNTY_SIGNAL_IMPORT_SUMMARY,
    priorityTier: NOTIFICATION_PRIORITY_TIER.P3,
    requiredPermission: PERMISSIONS.VIEW_APPLICATIONS.name,
  },
  [PLATFORM_NOTIFICATION_ACTION_TYPE.SIGNAL_IMPORT_COMPLETED]: {
    type: PLATFORM_NOTIFICATION_ACTION_TYPE.SIGNAL_IMPORT_COMPLETED,
    priorityTier: NOTIFICATION_PRIORITY_TIER.P3,
    requiredPermission: PERMISSIONS.VIEW_TESTS.name,
  },
  [PLATFORM_NOTIFICATION_ACTION_TYPE.INITIAL_TESTING_COMPLETE]: {
    type: PLATFORM_NOTIFICATION_ACTION_TYPE.INITIAL_TESTING_COMPLETE,
    priorityTier: NOTIFICATION_PRIORITY_TIER.P3,
    requiredPermission: PERMISSIONS.VIEW_APPLICATIONS.name,
  },
};

export const getActionRequiredPermission = ({
  type,
}: {
  type: PlatformNotificationActionType;
}): Permission => PLATFORM_ACTION_CATALOG[type].requiredPermission;

export const isPlatformNotificationActionPermitted = ({
  type,
  userPermissions,
}: {
  type: PlatformNotificationActionType;
  userPermissions: readonly string[];
}): boolean =>
  userPermissions.some((userPermission) =>
    permissionSatisfies(
      userPermission as Permission,
      getActionRequiredPermission({ type }),
    ),
  );

export const ALL_PLATFORM_NOTIFICATION_ACTION_TYPES: PlatformNotificationActionType[] =
  Object.values(PLATFORM_ACTION_CATALOG).map((entry) => entry.type);

export const getPermittedPlatformNotificationActionTypes = ({
  userPermissions,
}: {
  userPermissions: readonly string[];
}): PlatformNotificationActionType[] =>
  ALL_PLATFORM_NOTIFICATION_ACTION_TYPES.filter((type) =>
    isPlatformNotificationActionPermitted({ type, userPermissions }),
  );

export const getActionPriorityTier = ({
  type,
}: {
  type: PlatformNotificationActionType;
}): NotificationPriorityTier => PLATFORM_ACTION_CATALOG[type].priorityTier;

export type FindingActionContext = {
  findingId?: string;
  reportId?: string;
  targetId?: string;
  inProgressEnteredAt?: string;
  retestRunId?: string;
  severityChangeRequestId?: string;
  previousSeverity?: string;
  newSeverity?: string;
};

export type MemoryActionContext = {
  memoryRecordId: string;
};

export type SignalImportActionContext = {
  batchId: string;
  targetType?: TargetTypeType;
};

export const resolveSignalImportCompletedLifecyclePrefix = ({
  targetType,
}: {
  targetType?: TargetTypeType;
}): string => {
  if (targetType === TargetTypeSchema.enum.EXTERNAL_NETWORK) {
    return 'external-network';
  }

  return 'application';
};

export type BugBountyImportActionContext = {
  batchId?: string;
  scanRunKey?: string;
};

export type ActionLifecycleContext = FindingActionContext &
  Partial<MemoryActionContext> &
  Partial<SignalImportActionContext> &
  Partial<BugBountyImportActionContext>;

export const ENTITY_VIEWED_ACTION_TYPES: Partial<
  Record<NotificationEntityType, readonly PlatformNotificationActionType[]>
> = {
  [NOTIFICATION_ENTITY_TYPE.FINDING]: [
    PLATFORM_NOTIFICATION_ACTION_TYPE.REVIEW_RETEST_STILL_VULNERABLE,
    PLATFORM_NOTIFICATION_ACTION_TYPE.SEVERITY_CHANGE_APPROVED,
    PLATFORM_NOTIFICATION_ACTION_TYPE.SEVERITY_CHANGE_REJECTED,
  ],
  [NOTIFICATION_ENTITY_TYPE.MEMORY]: [
    PLATFORM_NOTIFICATION_ACTION_TYPE.REVIEW_NEW_MEMORY,
  ],
};

export const buildActionLifecycleKey = ({
  type,
  context,
}: {
  type: PlatformNotificationActionType;
  context: ActionLifecycleContext;
}): string => {
  switch (type) {
    case PLATFORM_NOTIFICATION_ACTION_TYPE.TRIAGE_NEW: {
      if (!context.findingId) {
        throw new Error('findingId is required for triage-new lifecycle keys');
      }

      return `finding:${context.findingId}:triage-new`;
    }

    case PLATFORM_NOTIFICATION_ACTION_TYPE.CONTINUE_REMEDIATION: {
      if (!context.findingId) {
        throw new Error(
          'findingId is required for continue remediation lifecycle keys',
        );
      }

      if (!context.inProgressEnteredAt) {
        throw new Error(
          'inProgressEnteredAt is required for continue remediation lifecycle keys',
        );
      }

      return `finding:${context.findingId}:continue-remediation:${context.inProgressEnteredAt}`;
    }

    case PLATFORM_NOTIFICATION_ACTION_TYPE.REVIEW_RETEST_STILL_VULNERABLE: {
      if (!context.findingId) {
        throw new Error(
          'findingId is required for review retest still vulnerable lifecycle keys',
        );
      }

      if (!context.retestRunId) {
        throw new Error(
          'retestRunId is required for review retest still vulnerable lifecycle keys',
        );
      }

      return `finding:${context.findingId}:retest-vulnerable:${context.retestRunId}`;
    }

    case PLATFORM_NOTIFICATION_ACTION_TYPE.INVESTIGATE_RETEST_FAILURE: {
      if (!context.findingId) {
        throw new Error(
          'findingId is required for investigate retest failure lifecycle keys',
        );
      }

      if (!context.retestRunId) {
        throw new Error(
          'retestRunId is required for investigate retest failure lifecycle keys',
        );
      }

      return `finding:${context.findingId}:retest-failed:${context.retestRunId}`;
    }

    case PLATFORM_NOTIFICATION_ACTION_TYPE.SEVERITY_CHANGE_APPROVED: {
      if (!context.findingId) {
        throw new Error(
          'findingId is required for severity change approved lifecycle keys',
        );
      }

      if (!context.severityChangeRequestId) {
        throw new Error(
          'severityChangeRequestId is required for severity change approved lifecycle keys',
        );
      }

      return `finding:${context.findingId}:severity-change-approved:${context.severityChangeRequestId}`;
    }

    case PLATFORM_NOTIFICATION_ACTION_TYPE.SEVERITY_CHANGE_REJECTED: {
      if (!context.findingId) {
        throw new Error(
          'findingId is required for severity change rejected lifecycle keys',
        );
      }

      if (!context.severityChangeRequestId) {
        throw new Error(
          'severityChangeRequestId is required for severity change rejected lifecycle keys',
        );
      }

      return `finding:${context.findingId}:severity-change-rejected:${context.severityChangeRequestId}`;
    }

    case PLATFORM_NOTIFICATION_ACTION_TYPE.REVIEW_NEW_MEMORY: {
      if (
        typeof context.memoryRecordId !== 'string' ||
        !context.memoryRecordId
      ) {
        throw new Error(
          'memoryRecordId is required for review-new memory lifecycle keys',
        );
      }

      return `memory:${context.memoryRecordId}:review-new`;
    }

    case PLATFORM_NOTIFICATION_ACTION_TYPE.NEW_SCHEDULED_REPORT: {
      if (!context.targetId || !context.reportId) {
        throw new Error(
          'targetId and reportId are required for new scheduled report lifecycle keys',
        );
      }

      return `application:${context.targetId}:scheduled-report:${context.reportId}`;
    }

    case PLATFORM_NOTIFICATION_ACTION_TYPE.SIGNAL_IMPORT_COMPLETED: {
      if (!context.targetId || !context.batchId) {
        throw new Error(
          'targetId and batchId are required for signal import lifecycle keys',
        );
      }

      const prefix = resolveSignalImportCompletedLifecyclePrefix({
        targetType: context.targetType,
      });

      return `${prefix}:${context.targetId}:signal-import:${context.batchId}`;
    }

    case PLATFORM_NOTIFICATION_ACTION_TYPE.BUG_BOUNTY_SIGNAL_IMPORT_SUMMARY: {
      if (context.scanRunKey) {
        return `import-scan:${context.scanRunKey}:bug-bounty-summary`;
      }

      if (!context.batchId) {
        throw new Error(
          'batchId or scanRunKey is required for bug bounty import summary lifecycle keys',
        );
      }

      return `import-batch:${context.batchId}:bug-bounty-summary`;
    }

    case PLATFORM_NOTIFICATION_ACTION_TYPE.INITIAL_TESTING_COMPLETE: {
      if (!context.targetId) {
        throw new Error(
          'targetId is required for initial testing complete lifecycle keys',
        );
      }

      return `application:${context.targetId}:initial-testing-complete`;
    }

    default: {
      const exhaustiveCheck: never = type;

      throw new Error(`Unsupported action type: ${exhaustiveCheck}`);
    }
  }
};

export const resolveActionLifecycleKey = ({
  type,
  lifecycleKey,
  lifecycleContext,
}: {
  type: PlatformNotificationActionType;
  lifecycleKey?: string;
  lifecycleContext?: ActionLifecycleContext;
}): string => {
  if (lifecycleKey) {
    return lifecycleKey;
  }

  if (!lifecycleContext) {
    throw new Error('lifecycleContext is required when lifecycleKey is absent');
  }

  return buildActionLifecycleKey({ type, context: lifecycleContext });
};
